import { useRef, useState, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, PenTool, Loader2, Eraser } from "lucide-react";

export default function AcceptProposal({ quote, onAccepted, submitUrl, autoOpen = false }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(quote.client_name || "");
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);

  useEffect(() => {
    if (autoOpen && !quote.signed) setOpen(true);
  }, [autoOpen, quote.signed]);

  const clear = () => sigRef.current?.clear();

  const submit = async () => {
    if (!name.trim()) { toast.error("Enter your full name"); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error("Please sign in the box"); return; }
    setSaving(true);
    try {
      const signature = sigRef.current.getCanvas().toDataURL("image/png");
      const { data } = await api.post(submitUrl || `/quotes/${quote.id}/accept`, { signer_name: name.trim(), signature });
      toast.success("Proposal accepted & signed");
      setOpen(false);
      onAccepted?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record acceptance");
    } finally {
      setSaving(false);
    }
  };

  if (quote.signed) {
    return (
      <div className="border border-green-300 bg-green-50 p-5" data-testid="signed-state">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="h-5 w-5" />
          <p className="font-display font-bold">Proposal Accepted</p>
        </div>
        {quote.signature && (
          <img src={quote.signature} alt="signature" className="mt-3 h-16 bg-white border border-green-200 object-contain px-2" data-testid="signature-image" />
        )}
        <p className="mt-2 font-mono text-xs text-zinc-600">
          Signed by <strong>{quote.signer_name}</strong>
          {quote.accepted_at ? ` · ${new Date(quote.accepted_at).toLocaleDateString()}` : ""}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="border border-zinc-900 bg-white p-5" data-testid="accept-panel">
        <div className="flex items-center gap-2">
          <PenTool className="h-5 w-5 text-primary" />
          <p className="font-display font-bold">Ready to proceed?</p>
        </div>
        <p className="mt-1 text-sm text-zinc-600">Accept this proposal with your online signature.</p>
        <Button
          onClick={() => setOpen(true)}
          data-testid="accept-proposal-btn"
          className="w-full mt-4 rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest h-11"
        >
          Accept Proposal
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="signature-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Accept & Sign</DialogTitle>
            <DialogDescription>Type your name and sign below to formally accept this proposal.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest text-zinc-600">Full name</Label>
              <Input data-testid="signer-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 rounded-none" placeholder="Jane Doe" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="font-mono text-xs uppercase tracking-widest text-zinc-600">Signature</Label>
                <button onClick={clear} type="button" data-testid="clear-signature-btn" className="flex items-center gap-1 text-xs text-zinc-500 hover:text-primary">
                  <Eraser className="h-3.5 w-3.5" /> Clear
                </button>
              </div>
              <div className="border border-zinc-300 bg-zinc-50">
                <SignatureCanvas
                  ref={sigRef}
                  penColor="#0A0A0A"
                  canvasProps={{ width: 400, height: 160, className: "w-full touch-none", "data-testid": "signature-canvas" }}
                />
              </div>
              <p className="mt-1 text-[11px] text-zinc-400">Draw with your mouse or finger.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="confirm-accept-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accept & Sign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
